import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../api/bookings';
import { AdminStorage, CustomerStorage } from '../../utils/storage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Lock, Mail, Shield, KeyRound, RefreshCw } from 'lucide-react';
import SiteBanner from '../../components/SiteBanner';

export default function AdminLogin() {
  const navigate = useNavigate();
  
  // Step 1: Email + Password
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Step 2: OTP Verification
  const [step, setStep] = useState(1); // 1 = credentials, 2 = OTP
  const [mfaToken, setMfaToken] = useState('');
  const [otp, setOtp] = useState('');
  
  // UI State
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendCount, setResendCount] = useState(0);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // SECURITY: Clear all cached profiles before admin login
      CustomerStorage.clearAuthOnly();
      AdminStorage.clear();

      const response = await adminAPI.login(email, password);

      // Check if MFA is required
      if (response.requires_mfa && response.mfa_token) {
        setMfaToken(response.mfa_token);
        setStep(2);
        toast.success('Verification code sent to your email');
      } else {
        // Legacy flow (shouldn't happen with new MFA system)
        AdminStorage.setAdmin(response.admin, response.admin.role);
        toast.success('Welcome, Admin!');
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Admin login error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Invalid admin credentials';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 5) {
      toast.error('Please enter the 5-digit code');
      return;
    }
    
    setLoading(true);
    try {
      const response = await adminAPI.verifyMFA(mfaToken, otp);

      // Cache the admin profile (sessionStorage, auto-clears on tab close).
      // The actual session lives in the httpOnly cookie the backend just set.
      AdminStorage.setAdmin(response.admin, response.admin.role);
      toast.success('Welcome, Admin!');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('MFA verification error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Invalid verification code';
      toast.error(errorMsg);
      
      // Clear OTP on error
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) {
      toast.error(`Please wait ${resendCooldown} seconds before resending`);
      return;
    }
    
    setLoading(true);
    try {
      const response = await adminAPI.resendMFA(mfaToken);

      setResendCount(response.resend_count || 0);
      setResendCooldown(60); // 60 second cooldown
      toast.success('New verification code sent!');
      setOtp(''); // Clear OTP field
    } catch (error) {
      console.error('Resend OTP error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to resend code';
      toast.error(errorMsg);
      
      // If session expired, go back to step 1
      if (error.response?.status === 401) {
        setStep(1);
        setMfaToken('');
        setOtp('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setStep(1);
    setMfaToken('');
    setOtp('');
    setResendCooldown(0);
    setResendCount(0);
  };

  return (
    <div className="admin-container flex flex-col min-h-screen bg-gradient-to-br from-emerald-50 to-emerald-100">
      <SiteBanner scope="admin" />
      <div className="flex-1 flex items-center justify-center p-6">
      <Card className="p-8 max-w-md w-full shadow-2xl" data-testid="admin-login-form">
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
            <Shield className="w-8 h-8 text-white" />
          </div>
        </div>
        <h1 className="text-3xl font-black font-heading uppercase text-center mb-2 text-emerald-700">Admin Login</h1>
        <p className="text-center text-emerald-600 mb-6 font-semibold">SmashZone Management</p>

        {/* STEP 1: Email + Password */}
        {step === 1 && (
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <Label htmlFor="admin-email" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="admin-email"
                  data-testid="admin-email-input"
                  type="email"
                  placeholder="admin@smashzone.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-14 rounded-2xl bg-secondary border-transparent pl-12"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="admin-password" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="admin-password"
                  data-testid="admin-password-input"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-14 rounded-2xl bg-secondary border-transparent pl-12"
                  required
                />
              </div>
            </div>
            <Button
              type="submit"
              data-testid="admin-login-button"
              className="w-full btn-volt h-14 mt-6"
              disabled={loading}
            >
              {loading ? 'Sending Code...' : 'Continue'}
            </Button>
            <div className="text-center pt-1">
              <Button
                type="button"
                variant="link"
                onClick={() => navigate('/admin/forgot-password')}
                data-testid="admin-forgot-password-link"
                className="text-sm text-emerald-700 p-0 h-auto"
              >
                Forgot password?
              </Button>
            </div>
          </form>
        )}

        {/* STEP 2: OTP Verification */}
        {step === 2 && (
          <div className="space-y-5">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <KeyRound className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-emerald-900">Check your email</p>
                  <p className="text-xs text-emerald-700 mt-1">
                    We sent a 5-digit code to <span className="font-semibold">{email}</span>
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleVerifyOTP} className="space-y-5">
              <div>
                <Label htmlFor="otp-input" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                  Verification Code
                </Label>
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="otp-input"
                    data-testid="otp-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={5}
                    placeholder="12345"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    className="h-14 rounded-2xl bg-secondary border-transparent pl-12 text-center text-2xl tracking-widest font-bold"
                    required
                    autoFocus
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="verify-otp-button"
                className="w-full btn-volt h-14"
                disabled={loading || otp.length !== 5}
              >
                {loading ? 'Verifying...' : 'Verify & Login'}
              </Button>
            </form>

            {/* Resend OTP */}
            <div className="flex items-center justify-center gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={handleResendOTP}
                disabled={loading || resendCooldown > 0 || resendCount >= 3}
                className="text-sm"
                data-testid="resend-otp-button"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {resendCooldown > 0 
                  ? `Resend in ${resendCooldown}s` 
                  : resendCount >= 3 
                    ? 'Max resends reached' 
                    : 'Resend Code'
                }
              </Button>
            </div>

            {resendCount > 0 && resendCount < 3 && (
              <p className="text-xs text-center text-muted-foreground">
                Resent {resendCount}/3 times
              </p>
            )}

            {/* Back to login */}
            <div className="pt-4 border-t">
              <Button
                type="button"
                variant="ghost"
                onClick={handleBackToLogin}
                className="w-full text-sm"
                data-testid="back-to-step1-button"
              >
                ← Back to Login
              </Button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="mt-6 text-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              data-testid="back-to-customer-button"
              className="text-sm"
            >
              Back to Customer Login
            </Button>
          </div>
        )}
      </Card>
      </div>
    </div>
  );
}